import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, Inbox, ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import emblem from "@/assets/golden-wine-emblem.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtDate = (iso) =>
  new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [status, setStatus] = useState(location.state?.user ? "ready" : "checking"); // checking | anon | ready | denied
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(data);
      setStatus("ready");
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) setStatus("denied");
      else if (code === 401) setStatus("anon");
      else {
        setStatus("ready");
        toast.error("No se pudieron cargar las solicitudes. Inténtalo de nuevo.");
      }
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
        if (data.is_admin) loadLeads();
        else setStatus("denied");
      } catch (e) {
        setStatus("anon");
      }
    };
    if (location.state?.user) {
      if (location.state.user.is_admin) loadLeads();
      else setStatus("denied");
    } else {
      init();
    }
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      /* noop */
    }
    setUser(null);
    setStatus("anon");
  };

  const totalValue = leads.reduce((s, l) => s + (l.total_price || 0), 0);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#05070A] text-gray-200">
      <header className="border-b border-[#F5CB5C]/15 bg-[#0B0E14]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={emblem} alt="Láser Vintage" className="w-10 h-10 object-contain drop-shadow-[0_0_12px_rgba(245,203,92,0.3)]" />
            <span className="font-display text-white tracking-[0.14em] text-sm sm:text-base">PANEL DE SOLICITUDES</span>
          </a>
          {status === "ready" && (
            <div className="flex items-center gap-3">
              {user?.email && <span className="hidden sm:block text-xs text-gray-400">{user.email}</span>}
              <button
                data-testid="admin-logout-button"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-gray-300 hover:border-[#F5CB5C]/50 hover:text-white transition-all"
              >
                <LogOut size={14} /> Salir
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {status === "checking" && (
          <div data-testid="admin-loading" className="py-32 text-center text-sm text-gray-500 animate-pulse tracking-[0.2em] uppercase">
            Comprobando acceso…
          </div>
        )}

        {status === "anon" && (
          <div data-testid="admin-login" className="max-w-md mx-auto mt-16 glass-card rounded-3xl p-8 text-center">
            <img src={emblem} alt="Láser Vintage" className="mx-auto w-16 h-16 object-contain drop-shadow-[0_0_16px_rgba(245,203,92,0.4)]" />
            <h1 className="mt-5 font-display text-white text-2xl">Área privada</h1>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              Inicia sesión con tu cuenta de Google autorizada para ver las solicitudes de presupuesto recibidas.
            </p>
            <button
              data-testid="admin-google-login-button"
              onClick={startGoogleLogin}
              className="mt-7 w-full inline-flex items-center justify-center gap-3 rounded-full px-6 py-3.5 text-sm font-bold text-[#05070A] bg-gradient-to-br from-[#FAE19C] via-[#F5CB5C] to-[#C69222] hover:shadow-[0_0_28px_rgba(245,203,92,0.4)] transition-all"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#05070A" d="M21.35 11.1H12v2.9h5.35c-.25 1.5-1.9 4.4-5.35 4.4a5.9 5.9 0 010-11.8c1.7 0 2.85.72 3.5 1.35l2.4-2.3C16.9 4.2 14.7 3.3 12 3.3a8.7 8.7 0 100 17.4c5 0 8.3-3.5 8.3-8.45 0-.57-.06-1-.15-1.55z" />
              </svg>
              Iniciar sesión con Google
            </button>
          </div>
        )}

        {status === "denied" && (
          <div data-testid="admin-denied" className="max-w-md mx-auto mt-16 glass-card rounded-3xl p-8 text-center">
            <span className="mx-auto w-14 h-14 rounded-2xl bg-[#540F20]/40 border border-[#A31D3D]/60 flex items-center justify-center">
              <ShieldAlert size={26} className="text-[#F5CB5C]" />
            </span>
            <h1 className="mt-5 font-display text-white text-2xl">Acceso restringido</h1>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              La cuenta {user?.email ? <strong className="text-gray-200">{user.email}</strong> : "actual"} no está autorizada para
              ver este panel. Contacta con el administrador.
            </p>
            <button
              data-testid="admin-logout-denied-button"
              onClick={logout}
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm text-gray-300 hover:border-[#F5CB5C]/50 hover:text-white transition-all"
            >
              <LogOut size={15} /> Cambiar de cuenta
            </button>
          </div>
        )}

        {status === "ready" && (
          <div data-testid="admin-dashboard">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div>
                <h1 className="font-display text-white text-2xl sm:text-3xl">Solicitudes de presupuesto</h1>
                <p className="mt-2 text-sm text-gray-400">Todas las peticiones recibidas desde la web, más recientes primero.</p>
              </div>
              <button
                data-testid="admin-refresh-button"
                onClick={loadLeads}
                className="inline-flex items-center gap-2 rounded-full border border-[#F5CB5C]/30 px-4 py-2 text-xs text-[#F5CB5C] hover:bg-[#F5CB5C]/10 transition-all self-start"
              >
                <RefreshCw size={14} className={loadingLeads ? "animate-spin" : ""} /> Actualizar
              </button>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              <div data-testid="admin-stat-total" className="glass-card rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Total solicitudes</div>
                <div className="mt-1 font-display text-2xl text-white">{leads.length}</div>
              </div>
              <div className="glass-card rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Valor estimado</div>
                <div className="mt-1 font-display text-2xl text-gold-gradient">{eur(totalValue)}</div>
              </div>
              <div className="glass-card rounded-2xl p-4 col-span-2 sm:col-span-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Última</div>
                <div className="mt-1 text-sm text-gray-300">{leads[0] ? fmtDate(leads[0].created_at) : "—"}</div>
              </div>
            </div>

            {leads.length === 0 ? (
              <div data-testid="admin-empty" className="mt-8 glass-card rounded-3xl p-12 text-center">
                <Inbox size={34} className="mx-auto text-gray-600" />
                <p className="mt-4 text-sm text-gray-500">Todavía no hay solicitudes. Aparecerán aquí en cuanto lleguen.</p>
              </div>
            ) : (
              <div className="mt-6 glass-card rounded-3xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm" data-testid="admin-leads-table">
                    <thead>
                      <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.16em] text-gray-500">
                        <th className="p-4 font-semibold">Fecha</th>
                        <th className="p-4 font-semibold">Cliente</th>
                        <th className="p-4 font-semibold">Contacto</th>
                        <th className="p-4 font-semibold">Producto</th>
                        <th className="p-4 font-semibold text-right">Cantidad</th>
                        <th className="p-4 font-semibold text-right">Estimación</th>
                        <th className="p-4 font-semibold">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} data-testid="admin-lead-row" className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] align-top">
                          <td className="p-4 text-xs text-gray-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                          <td className="p-4">
                            <div className="text-white font-medium">{l.name}</div>
                            <div className="text-xs text-gray-500">{l.company}</div>
                          </td>
                          <td className="p-4 text-xs text-gray-400">
                            <a href={`mailto:${l.email}`} className="hover:text-[#F5CB5C]">{l.email}</a>
                            <div>
                              <a href={`tel:${l.phone}`} className="hover:text-[#F5CB5C]">{l.phone}</a>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="rounded-full bg-[#540F20]/40 border border-[#A31D3D]/50 px-2.5 py-1 text-[11px] text-[#FAE19C] whitespace-nowrap">
                              {l.product_label}
                            </span>
                          </td>
                          <td className="p-4 text-right text-gray-300 whitespace-nowrap">{l.quantity} u.</td>
                          <td className="p-4 text-right whitespace-nowrap">
                            {l.total_price > 0 ? (
                              <>
                                <div className="text-gold-gradient font-display">{eur(l.total_price)}</div>
                                <div className="text-[11px] text-gray-600">{eur(l.unit_price)}/ud</div>
                              </>
                            ) : (
                              <span className="text-[11px] text-gray-400">Según modelo</span>
                            )}
                          </td>
                          <td className="p-4 text-xs text-gray-500 max-w-[220px]">{l.details || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
